import { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import { ArrowLeft } from "lucide-react"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"
import { StudentStore } from "@/hooks/stores/StudentStore"
import api from "@/hooks/api/apiClient"
import config from "../../../config"
import { WelcomeStep } from "./steps/WelcomeStep"
import { ProfileStep } from "./steps/ProfileStep"
import { StreamStep } from "./steps/StreamStep"
import { UploadResultsStep } from "./steps/UploadResultsStep"
import { AvatarStep } from "./steps/AvatarStep"
import { IntroStep } from "./steps/IntroStep"

const STEPS = ["welcome", "profile", "stream", "results", "avatar", "intro"]

function ProgressBar({ step, total }) {
  return (
    <div className="flex gap-1.5 mb-8">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
            i <= step ? "bg-indigo-600" : "bg-slate-200"
          }`}
        />
      ))}
    </div>
  )
}

export default function OnboardingWizard() {
  const navigate = useNavigate()
  const [stepIndex, setStepIndex] = useState(0)
  const [data, setData] = useState({})

  // Non-students have no onboarding — redirect immediately
  const userType = AuthStore.getState().currentUser?.userType
  useEffect(() => {
    if (userType && userType !== "student") {
      navigate("/school-dashboard", { replace: true })
    }
  }, [userType, navigate])

  const saveToStudent = (updates) => {
    // Update Zustand store (merges preferences correctly)
    if (updates.preferences) {
      StudentStore.getState().updatePreferences(updates.preferences)
    }
    if (updates.metadata) {
      StudentStore.getState().updateMetadata(updates.metadata)
    }
    const { preferences: _prefs, metadata: _meta, ...rest } = updates
    if (Object.keys(rest).length) {
      StudentStore.getState().updateStudent(rest)
    }

    // Also keep prototype localStorage in sync
    if (config.appMode === "prototype") {
      const user = AuthStore.getState().currentUser
      if (!user) return
      const students = StorageService.read("students")
      const idx = students.findIndex(
        (s) => s.userId === user._id || s._id === user._id || s.email === user.email
      )
      if (idx !== -1) {
        const student = students[idx]
        students[idx] = {
          ...student,
          ...updates,
          preferences: { ...(student.preferences ?? {}), ...(updates.preferences ?? {}) },
        }
        StorageService.write("students", students)
      }
      return
    }

    // Production: sync to backend
    api.patch("/students/me", updates).catch(() => {})
  }

  const goBack = () => {
    if (stepIndex > 0) setStepIndex((i) => i - 1)
  }

  const advance = (stepData = {}) => {
    const merged = { ...data, ...stepData }
    setData(merged)

    // Persist partial data as each step completes
    const prefUpdates = {}
    if (stepData.firstName)   saveToStudent({ firstName: stepData.firstName, lastName: stepData.lastName })
    if (stepData.country)     prefUpdates.country = stepData.country
    if (stepData.examBody)    prefUpdates.examBody = stepData.examBody
    if (stepData.classLevel)  prefUpdates.classLevel = stepData.classLevel
    if (stepData.classes)     prefUpdates.classes = stepData.classes
    if (stepData.stream)      prefUpdates.stream = stepData.stream
    if (Object.keys(prefUpdates).length) saveToStudent({ preferences: prefUpdates })
    if (stepData.academicResults?.length) saveToStudent({ academicResults: stepData.academicResults })
    if (stepData.avatar)      saveToStudent({ avatar: stepData.avatar })

    if (stepIndex < STEPS.length - 1) {
      setStepIndex((i) => i + 1)
    } else {
      // Final step — mark onboarding complete and navigate
      saveToStudent({ metadata: { onboardingCompleted: true } })
      navigate("/psychometric-assessment", { replace: true })
    }
  }

  const currentUser = AuthStore.getState().currentUser
  const studentRecord = StudentStore.getState().student

  const stepName = STEPS[stepIndex]

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl p-6 sm:p-8">
        {/* Back button — hidden on welcome step */}
        {stepIndex > 0 && (
          <button
            onClick={goBack}
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        )}
        <ProgressBar step={stepIndex} total={STEPS.length} />

        {stepName === "welcome" && (
          <WelcomeStep onNext={() => advance()} />
        )}
        {stepName === "profile" && (
          <ProfileStep
            defaultData={{
              firstName: studentRecord?.firstName ?? currentUser?.firstName ?? currentUser?.name?.split(" ")[0] ?? "",
              lastName: studentRecord?.lastName ?? currentUser?.lastName ?? currentUser?.name?.split(" ").slice(1).join(" ") ?? "",
              country: studentRecord?.preferences?.country ?? "",
              examBody: studentRecord?.preferences?.examBody ?? "",
              classLevel: studentRecord?.preferences?.classLevel ?? "",
            }}
            onNext={advance}
          />
        )}
        {stepName === "stream" && (
          <StreamStep onNext={advance} />
        )}
        {stepName === "results" && (
          <UploadResultsStep onNext={advance} />
        )}
        {stepName === "avatar" && (
          <AvatarStep onNext={advance} />
        )}
        {stepName === "intro" && (
          <IntroStep onNext={() => advance()} />
        )}
      </div>
    </div>
  )
}
